import { type CommunityEntry, type SavedConfig } from "@cfg/api-contract";
import { compareSemver, portParams, type ParamSpec, type PrintTips } from "@cfg/design-sdk";
import {
  ConfiguratorProvider,
  ParamForm,
  PresetBar,
  Viewport,
  WidgetRegistryProvider,
  buildConfiguratorSearch,
  getDraft,
  paramsKey,
  parseConfiguratorUrl,
  scrollBehavior,
  useChanged,
  useConfiguratorContext,
  useConfiguratorStore,
  useDesign,
  useExport,
  useFlash,
  useLocale,
  useMesh,
  useParams,
  type Draft,
} from "@cfg/configurator-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams as useRouteParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { useCatalog } from "../catalog.ts";
import { configName } from "../config-name.ts";
import { addDownload, answerDownload, pendingDownload, readDownloadLog, writeDownloadLog, type DownloadRecord } from "../download-log.ts";
import { formatDownloads, useDownloadStats } from "../downloads.ts";
import { errorCopyKey, errorKind } from "../errors.ts";
import { getKernelClient } from "../kernel.ts";
import { displayTitle } from "../titles.ts";
import { CommunityPanel, communityQueryKey, useDesignCommunity } from "./CommunityPanel.tsx";
import { DefaultConfiguratorFrame, DefaultEmptyState } from "./defaults.tsx";
import { CommunityEntryBar } from "./CommunityEntryBar.tsx";
import { MyBracket } from "./MyBracket.tsx";
import { StartFromSet } from "./StartFromSet.tsx";
import { AfterDownload, DownloadReminder } from "./AfterDownload.tsx";

/** How long the result line under the downloads stays up. */
const LIVE_MS = 6000;
/** Model and panel side by side (the layout's breakpoint in styles.css and the brand CSS). */
const WIDE_LAYOUT = "(min-width: 901px)";

/** Whether `query` matches, following changes (window resize, rotation). */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const on = () => setMatches(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, [query]);
  return matches;
}

interface Opened {
  designId: string;
  version: string;
  bundleUrl: string;
  latest: string;
  deprecated: boolean;
  params: Record<string, unknown>;
  /** Saved config (server) or draft (local) this was opened from. */
  saved?: { kind: "config"; config: SavedConfig } | { kind: "draft"; draft: Draft };
  /** Community entry to apply once its list loads (`?community=<id>`). */
  communityId?: string;
  /** Set to open the community tab filtered to (`?fit=<ref>`, from the home page). */
  fit?: string;
}

/** Placeholder rows for the parameter panel while the design loads. */
function PanelSkeleton() {
  return (
    <div className="cfg-skeleton" aria-hidden>
      <div className="cfg-skeleton-block" style={{ height: "1.4em", width: "45%" }} />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="cfg-skeleton-block" style={{ height: "3.2em" }} />
      ))}
      <div className="cfg-skeleton-block" style={{ height: "2.8em", marginTop: "0.5em" }} />
    </div>
  );
}

/** The configurator frame (stage + panel placeholders) shown before the design data arrives. */
function ConfiguratorSkeleton() {
  const { brand, copy: t } = useApp();
  const Frame = brand.slots?.ConfiguratorFrame ?? DefaultConfiguratorFrame;
  return (
    <section className="page page-configurator is-loading" aria-busy="true">
      <header className="page-head configurator-head">
        <div>
          <h1>
            <span className="cfg-visually-hidden">{t("configurator.loading")}</span>
            <span className="cfg-skeleton-block" aria-hidden style={{ display: "inline-block", width: "12ch", height: "1em" }} />
          </h1>
        </div>
      </header>
      <Frame>
        <div className="configurator-layout">
          <div className="configurator-stage">
            <div className="cfg-viewport cfg-viewport-skeleton" aria-hidden />
            <div className="stage-stats">
              <span>{t("configurator.loading")}</span>
            </div>
          </div>
          <aside className="configurator-panel">
            <PanelSkeleton />
          </aside>
        </div>
      </Frame>
    </section>
  );
}

function StatChip({ value, kind }: { value: string; kind: string }) {
  const changed = useChanged(value);
  return (
    <span className={`stage-stat stage-stat-${kind}`} data-changed={changed || undefined}>
      {value}
    </span>
  );
}

/**
 * "Mount: Wall | SKÅDIS": the other designs of this design's family (same
 * params, other variant). Switching opens the sibling with the current params.
 */
function FamilySwitch({ designId }: { designId: string }) {
  const { copy } = useApp();
  const { designs } = useLocale();
  const catalog = useCatalog();
  const navigate = useNavigate();
  const { params } = useParams();
  const labelId = useRef(`family-${Math.random().toString(36).slice(2, 8)}`).current;
  const me = catalog.data?.find((d) => d.id === designId);
  const members = me?.family ? (catalog.data ?? []).filter((d) => d.family === me.family) : [];
  if (members.length < 2) return null;
  const label = (d: (typeof members)[number]) => (d.variant ? (designs ? designs.text(d.id, d.variant) : d.variant) : d.title);
  return (
    <div className="family-switch" role="group" aria-labelledby={labelId}>
      <span className="cfg-presets-label" id={labelId}>
        {copy("family.label")}
      </span>
      <div className="family-options">
        {members.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`cfg-chip family-option${d.id === designId ? " is-active" : ""}`}
            aria-pressed={d.id === designId}
            title={d.id === designId ? d.title : `${d.title} · ${copy("family.hint")}`}
            onClick={() => d.id !== designId && navigate(`/design/${encodeURIComponent(d.id)}${buildConfiguratorSearch({ params })}`)}
          >
            {label(d)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ConfiguratorPage() {
  const { id = "" } = useRouteParams();
  const location = useLocation();
  const { registry, api, brand, copy } = useApp();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const urlState = useMemo(() => parseConfiguratorUrl(location.search), [location.search]);

  const opened = useQuery({
    queryKey: ["open", id, urlState.version, urlState.configId, urlState.fit],
    queryFn: async (): Promise<Opened | null> => {
      let params = urlState.params;
      let version = urlState.version;
      let saved: Opened["saved"];
      if (urlState.configId) {
        const draft = await getDraft(urlState.configId);
        if (draft) {
          saved = { kind: "draft", draft };
          params = draft.params;
          version = draft.version;
        } else if (api) {
          // Someone else's config (or a draft from another device, or signed out): not ours to open,
          // so open the params the link carries instead of failing.
          const config = await api.getConfig(urlState.configId).then(
            (r) => r.config,
            () => undefined,
          );
          if (config) {
            saved = { kind: "config", config };
            params = config.params;
            version = config.version;
          }
        }
      }
      const r = await registry.resolve(id, version);
      if (!r) return null;
      // Params handed over by the "update to <version>" flow.
      const handoverKey = `cfg-migrated:${id}@${r.version}`;
      const handover = sessionStorage.getItem(handoverKey);
      if (handover) {
        sessionStorage.removeItem(handoverKey);
        try {
          params = JSON.parse(handover) as Record<string, unknown>;
        } catch {
          /* ignore */
        }
      }
      return { designId: id, version: r.version, bundleUrl: r.bundleUrl, latest: r.latest, deprecated: r.deprecated, params, saved, communityId: urlState.communityId, fit: urlState.fit };
    },
  });

  if (opened.isLoading) return <ConfiguratorSkeleton />;
  const browse = (
    <a className="cfg-btn" href="/">
      {copy("share.browse")}
    </a>
  );
  if (!opened.data && !(opened.isError && errorKind(opened.error) !== "notFound"))
    return <Empty title={copy("configurator.notFound")} body={copy("configurator.notFoundBody", { design: `${id}${urlState.version ? `@${urlState.version}` : ""}` })} action={browse} />;
  if (opened.isError || !opened.data)
    return (
      <Empty
        title={copy("configurator.openError")}
        body={copy(errorCopyKey(opened.error))}
        action={
          <span className="df-empty-actions">
            <button type="button" className="cfg-btn cfg-btn-primary" onClick={() => void opened.refetch()}>
              {copy("common.retry")}
            </button>
            {browse}
          </span>
        }
      />
    );
  return <ConfiguratorScreen key={`${opened.data.designId}@${opened.data.version}:${urlState.configId ?? ""}:${urlState.fit ?? ""}`} opened={opened.data} />;
}

function ConfiguratorScreen({ opened }: { opened: Opened }) {
  const { brand, analytics } = useApp();
  const navigate = useNavigate();
  const client = getKernelClient();
  const key = `${opened.designId}@${opened.version}`;
  const defaultsRef = useRef<Record<string, unknown>>({});

  useEffect(() => analytics.track("view", opened.designId, opened.version), [analytics, opened.designId, opened.version]);

  const onParamsChange = useCallback(
    (params: Record<string, unknown>) => {
      const search = buildConfiguratorSearch(
        { designId: undefined, version: opened.version, params, configId: opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.id : opened.saved.config.id) : undefined },
        defaultsRef.current,
      );
      if (search !== window.location.search) window.history.replaceState(null, "", `${window.location.pathname}${search}`);
      analytics.track("configure", opened.designId, opened.version);
    },
    [opened, analytics],
  );

  return (
    <ConfiguratorProvider client={client} designKey={key} bundleUrl={opened.bundleUrl} initialParams={opened.params} onParamsChange={onParamsChange}>
      <WidgetRegistryProvider widgets={brand.widgets}>
        <Screen opened={opened} defaultsRef={defaultsRef} onOpenVersion={(v) => navigate(`/design/${encodeURIComponent(opened.designId)}?v=${v}`)} />
      </WidgetRegistryProvider>
    </ConfiguratorProvider>
  );
}

function Screen({ opened, defaultsRef, onOpenVersion }: { opened: Opened; defaultsRef: React.MutableRefObject<Record<string, unknown>>; onOpenVersion: (v: string) => void }) {
  const { brand, copy, locale, api, auth, analytics, registry } = useApp();
  const user = useAuthUser(auth);
  const queryClient = useQueryClient();
  const Frame = brand.slots?.ConfiguratorFrame ?? DefaultConfiguratorFrame;
  const { description, spec, status, error } = useDesign();
  const { params, reset, dirty } = useParams();
  const { stats } = useMesh();
  const t = copy;
  const { flash, done } = useFlash();
  const [live, setLive] = useState("");
  // The result line ("Saved…", "Downloaded…") clears itself, but not while it's pointed at or focused
  // (time to read it); its × clears it sooner.
  const [liveHeld, setLiveHeld] = useState(false);
  useEffect(() => {
    if (!live || liveHeld) return;
    const timer = setTimeout(() => setLive(""), LIVE_MS);
    return () => clearTimeout(timer);
  }, [live, liveHeld]);
  const holdLive = { onMouseEnter: () => setLiveHeld(true), onMouseLeave: () => setLiveHeld(false), onFocus: () => setLiveHeld(true), onBlur: () => setLiveHeld(false) };
  const wide = useMediaQuery(WIDE_LAYOUT);
  const { store, client } = useConfiguratorContext();
  const currentKey = useConfiguratorStore((s) => paramsKey(s));
  const features = brand.config.features;
  const gallery = features.publicGallery && !!api;
  const [tab, setTab] = useState<"params" | "community">(gallery && (opened.communityId || opened.fit) ? "community" : "params");
  const downloads = useDownloadStats();
  const community = useDesignCommunity(opened.designId);
  const catalog = useCatalog();
  const downloadCount = downloads.data?.[opened.designId] ?? 0;
  const titleOf = useCallback((designId: string) => catalog.data?.find((d) => d.id === designId)?.title ?? designId, [catalog.data]);
  const { designs } = useLocale();
  /** Mount name ("Wall", "SKÅDIS") of a design in a family of two or more; undefined otherwise. */
  const variantOf = useCallback(
    (designId: string) => {
      const d = catalog.data?.find((c) => c.id === designId);
      if (!d?.family || !d.variant || (catalog.data ?? []).filter((c) => c.family === d.family).length < 2) return undefined;
      return designs ? designs.text(d.id, d.variant) : d.variant;
    },
    [catalog.data, designs],
  );
  useEffect(() => {
    if (description) defaultsRef.current = description.defaults;
  }, [description, defaultsRef]);

  /**
   * The community entry put on the model, the params key right after, and the
   * user's own params from before the first one (for "Back to mine"). It is
   * "on the model" while the params still equal that key.
   */
  const [applied, setApplied] = useState<{ id: string; key: string; before: Record<string, unknown> } | null>(null);
  const onModel = applied && applied.key === currentKey ? applied : null;
  /** The entry on the model, live from the list (its tags, prints and likes change while it's there). */
  const onModelEntry = onModel ? community.data?.items.find((i) => i.id === onModel.id) : undefined;
  /** An entry's params for the design on the model: migrated from its version, or carried over from a family sibling. */
  const entryParams = async (e: CommunityEntry) => {
    const d = store.getState().description;
    if (!d) return null;
    let src = e.params;
    // Same design, other version: run its migrations; a family sibling's params are carried over as they are.
    if (e.designId === d.id && e.version !== d.version) src = await client.migrate(`${d.id}@${d.version}`, e.version, src).catch(() => src);
    return { ...d.defaults, ...portParams(src, d.spec as ParamSpec[]) };
  };
  const applyEntry = async (e: CommunityEntry) => {
    const next = await entryParams(e);
    if (!next) return;
    const before = applied?.before ?? store.getState().params;
    store.getState().replaceParams(next);
    setApplied({ id: e.id, key: paramsKey(store.getState()) ?? "", before });
  };
  /** "Start from a set": the best-printed dimensions for that set become the user's own to adjust. */
  const startFromSet = async (e: CommunityEntry, ref: string) => {
    const next = await entryParams(e);
    if (!next) return;
    setApplied(null);
    store.getState().replaceParams(next);
    setLive(copy("configurator.startedFromSet", { ref }));
  };
  /** A card picked in the Community tab: on phones the model is above the list, out of view, so bring it back and say so. */
  const pickEntry = async (e: CommunityEntry) => {
    await applyEntry(e);
    if (wide) return;
    document.querySelector(".configurator-stage")?.scrollIntoView({ block: "start", behavior: scrollBehavior() });
    setLive(copy("community.appliedLive"));
  };
  const restore = () => {
    if (applied) store.getState().replaceParams(applied.before);
    setApplied(null);
  };
  /** "Start from this one": the community bracket's dimensions become yours to change (the entry is untouched). */
  const adopt = () => {
    setApplied(null);
    setLive(copy("community.adopted"));
    requestAnimationFrame(() => document.querySelector<HTMLElement>("#cfg-tabpanel-params input:not([disabled]), #cfg-tabpanel-params select:not([disabled])")?.focus());
  };
  /** "See it in the community" from the save block: put that entry on the model and show the list. */
  const openEntry = (id: string) => {
    const e = community.data?.items.find((i) => i.id === id);
    if (e) void applyEntry(e);
    setTab("community");
  };
  /** "Next steps" after a download, for the params it was made from (`key`): hidden once they change. */
  const [next, setNext] = useState<{ key: string; file?: { filename: string; size: string }; entryId?: string; fit?: string } | null>(null);
  // A fresh card sits under the buttons, often below the fold: bring it into view.
  useEffect(() => {
    if (next) document.querySelector(".after-download")?.scrollIntoView({ block: "nearest", behavior: scrollBehavior() });
  }, [next]);
  /** "I printed it" for your own dimensions: MyBracket opens its tag step. */
  const [tagRequest, setTagRequest] = useState<{ nonce: number; fit?: { ref: string; name?: string } }>();
  /** "Did it fit?" for a download from an earlier visit to this design or its family; undefined until checked, null for none. */
  const visitStart = useRef(new Date().toISOString()).current;
  const [reminder, setReminder] = useState<DownloadRecord | null>();
  useEffect(() => {
    if (reminder !== undefined || !catalog.data) return;
    const me = catalog.data.find((d) => d.id === opened.designId);
    const ids = me?.family ? catalog.data.filter((d) => d.family === me.family).map((d) => d.id) : [opened.designId];
    setReminder(pendingDownload(readDownloadLog(brand.config.id), ids, visitStart) ?? null);
  }, [catalog.data]);
  const answer = (rec: Pick<DownloadRecord, "designId" | "params">) => writeDownloadLog(brand.config.id, answerDownload(readDownloadLog(brand.config.id), rec));
  /** Put a downloaded bracket's params back on the model as your own (migrated or carried over like a community entry's). */
  const loadParams = async (rec: DownloadRecord) => {
    const d = store.getState().description;
    if (!d) return;
    let src = rec.params;
    if (rec.designId === d.id && rec.version !== d.version) src = await client.migrate(`${d.id}@${d.version}`, rec.version, src).catch(() => src);
    store.getState().replaceParams({ ...d.defaults, ...portParams(src, d.spec as ParamSpec[]) });
    setApplied(null);
  };
  /** "Yes, I printed it": that bracket goes back on the model with the Next steps card's "Printed it?". */
  const reminderYes = async (rec: DownloadRecord) => {
    setReminder(null);
    answer(rec);
    const e = rec.entryId ? community.data?.items.find((i) => i.id === rec.entryId) : undefined;
    if (e) await applyEntry(e);
    else await loadParams(rec);
    setNext({ key: paramsKey(store.getState()) ?? "", entryId: e?.id, fit: rec.fit });
  };
  /** "Not quite": its dimensions go back on the model, editable, to adjust and download again. */
  const reminderNo = async (rec: DownloadRecord) => {
    setReminder(null);
    answer(rec);
    await loadParams(rec);
    setTab("params");
    setLive(copy("next.adjusting"));
  };
  /** ARIA tabs: one tab stop; arrows, Home and End switch tabs and move focus with them. */
  const onTabKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const order = ["params", "community"] as const;
    const i = order.indexOf(tab);
    const nextTab = e.key === "ArrowRight" ? order[(i + 1) % 2] : e.key === "ArrowLeft" ? order[(i + 1) % 2] : e.key === "Home" ? order[0] : e.key === "End" ? order[1] : null;
    if (!nextTab) return;
    e.preventDefault();
    setTab(nextTab);
    document.getElementById(`cfg-tab-${nextTab}`)?.focus();
  };
  /**
   * The panel scrolls on its own on wide screens, so Save and Tag can sit far below the fold: a small
   * "My bracket ↓" pinned to the panel's bottom while that block is out of view.
   */
  const [saveHidden, setSaveHidden] = useState(false);
  const showSaveJump = wide && tab === "params" && features.saveConfigs && !onModel;
  useEffect(() => {
    const block = document.querySelector(".panel-save");
    if (!showSaveJump || !block) return setSaveHidden(false);
    const io = new IntersectionObserver(([e]) => setSaveHidden(!e.isIntersecting), { threshold: 0.15 });
    io.observe(block);
    return () => io.disconnect();
  }, [showSaveJump, description]);
  /** The Next steps card's "Save": reveal the My bracket block's Save. */
  const goSave = () => {
    setTab("params");
    requestAnimationFrame(() => {
      const b = document.querySelector<HTMLButtonElement>(".cfg-save-btn");
      b?.scrollIntoView({ block: "center", behavior: scrollBehavior() });
      b?.focus({ preventScroll: true });
    });
  };

  const entryBar = onModelEntry && <CommunityEntryBar entry={onModelEntry} designId={opened.designId} onRestore={restore} floating={wide} />;

  /** One per signed-in user and design; credited to the community entry when its params were downloaded unchanged. */
  const recordDownload = () => {
    if (!api || !user) return;
    const fromEntry = onModel?.id;
    api
      .recordDownload(opened.designId, fromEntry ? { community: fromEntry } : {})
      .then((r) => {
        queryClient.setQueryData<Record<string, number>>(["downloads", brand.config.id], (old) => ({ ...old, [opened.designId]: r.downloadCount }));
        if (fromEntry && r.communityCount !== undefined)
          queryClient.setQueryData(communityQueryKey(brand.config.id, opened.designId), (old: typeof community.data) =>
            old && { ...old, items: old.items.map((i) => (i.id === fromEntry ? { ...i, downloadCount: r.communityCount! } : i)) },
          );
      })
      .catch(() => {
        /* counting must never get in the way of the file */
      });
  };
  const exporter = useExport((info) => {
    analytics.track("export", opened.designId, opened.version, { format: info.format, bytes: info.bytes });
    recordDownload();
    flash(`export-${info.format}`);
    const size = `${Math.max(1, Math.round(info.bytes / 1024))} KB`;
    // The Next steps card says it on wide screens; phones also get the short line over the bottom bar.
    if (!wide) setLive(t("configurator.live.exported", { filename: info.filename, size }));
    setNext({ key: currentKey ?? "", file: { filename: info.filename, size }, entryId: onModel?.id, fit: opened.fit });
    const log = addDownload(readDownloadLog(brand.config.id), {
      designId: opened.designId,
      version: opened.version,
      params,
      name: autoName,
      entryId: onModel?.id,
      fit: opened.fit,
      at: new Date().toISOString(),
    });
    writeDownloadLog(brand.config.id, log);
  });
  const scene = brand.scenes[brand.config.scene];
  const newer = compareSemver(opened.latest, opened.version) > 0 ? opened.latest : null;

  const upgrade = async () => {
    if (!newer) return;
    // Migrate the current params in the worker (design.migrateFrom), then open the
    // newer version with them; saving afterwards writes a new revision, never in place.
    const kernel = getKernelClient();
    const target = await registry.resolve(opened.designId, newer);
    if (target) await kernel.load(`${opened.designId}@${newer}`, target.bundleUrl);
    const migrated = await kernel.migrate(`${opened.designId}@${newer}`, opened.version, params).catch(() => params);
    sessionStorage.setItem(`cfg-migrated:${opened.designId}@${newer}`, JSON.stringify(migrated));
    onOpenVersion(newer);
  };

  const title = description ? displayTitle(String(description.meta.title ?? description.id)) : opened.designId;
  const autoName = description ? configName(title, spec, params, description.defaults) : "";
  const printNotes = description ? (description.meta.printNotes as string | undefined) : undefined;

  // Every param combination should print (`design build` reports the print check), so nothing blocks a download; a click during a rebuild waits for it ("Preparing…").
  // Signed-out visitors get one "Sign in to download" button; the params ride along in the URL through the redirect.
  const exportNeedsSignIn = features.exportRequiresSignIn && auth.enabled && !user;
  const exportDisabled = !description || status === "error" || !!exporter.busy;
  const exportLabel = (format: "stl" | "3mf", idle: string) =>
    exporter.busy === format ? (exporter.phase === "preparing" ? t("configurator.preparing") : t("configurator.exporting")) : done === `export-${format}` ? t("configurator.done.export") : idle;
  const btnState = (id: string, busy?: boolean) => `${done === id ? " is-done" : ""}${busy ? " is-busy" : ""}`;
  useEffect(() => {
    if (exporter.error) setLive(exporter.error);
  }, [exporter.error]);

  const sizeText = stats ? `${(stats.bbox.max[0] - stats.bbox.min[0]).toFixed(1)} × ${(stats.bbox.max[1] - stats.bbox.min[1]).toFixed(1)} × ${(stats.bbox.max[2] - stats.bbox.min[2]).toFixed(1)} mm` : "";

  // Downloads act on whatever is on the model: under the 3D view on wide screens (both tabs), else the
  // panel's bottom bar, on the Dimensions tab and, while a community bracket is on the model, on Community too.
  const actions = (
    <div className={`panel-actions${wide ? " stage-actions" : ""}`} hidden={!wide && tab !== "params" && !onModel}>
      {exportNeedsSignIn && (features.exportStl || features.export3mf) && (
        <button type="button" className="cfg-btn cfg-btn-primary cfg-btn-block cfg-export-signin" onClick={() => void auth.signIn()}>
          {copy("auth.signInToExport")}
        </button>
      )}
      {!exportNeedsSignIn && features.exportStl && (
        <button
          type="button"
          className={`cfg-btn cfg-btn-primary cfg-btn-block${btnState("export-stl", exporter.busy === "stl")}`}
          disabled={exportDisabled}
          data-phase={exporter.busy === "stl" ? exporter.phase : undefined}
          title={copy("configurator.export.stlHint")}
          onClick={exporter.exportStl}
        >
          {exportLabel("stl", copy("configurator.export.stl"))}
        </button>
      )}
      {!exportNeedsSignIn && features.export3mf && (
        <button
          type="button"
          className={`cfg-btn cfg-btn-block${btnState("export-3mf", exporter.busy === "3mf")}`}
          disabled={exportDisabled}
          data-phase={exporter.busy === "3mf" ? exporter.phase : undefined}
          title={copy("configurator.export.3mfHint")}
          onClick={exporter.export3mf}
        >
          {exportLabel("3mf", copy("configurator.export.3mf"))}
        </button>
      )}
      {/* Which file? Said once under the buttons on wide screens (phones keep the bottom bar short; the buttons carry it as a tooltip). */}
      {wide && !exportNeedsSignIn && features.exportStl && features.export3mf && <p className="cfg-format-hint muted small">{copy("configurator.export.formatHint")}</p>}
      <p className="cfg-live" role="status" aria-live="polite" {...holdLive}>
        {live && (
          <>
            <span className="cfg-live-text">{live}</span>
            <button type="button" className="cfg-live-close" aria-label={t("common.dismiss")} onClick={() => setLive("")}>
              ×
            </button>
          </>
        )}
      </p>
    </div>
  );

  const nextCard = next && next.key === currentKey && (
    <AfterDownload
      key={`${next.key}:${next.file?.filename ?? ""}`}
      file={next.file}
      designId={opened.designId}
      volumeCm3={stats ? stats.volume / 1000 : undefined}
      print={description?.meta.print as PrintTips | undefined}
      printNotes={printNotes}
      entry={next.entryId && onModel?.id === next.entryId ? onModelEntry : undefined}
      fit={next.fit}
      saved={new URLSearchParams(window.location.search).has("c")}
      onSave={goSave}
      onTag={(fit) => {
        answer({ designId: opened.designId, params });
        setTab("params");
        setTagRequest({ nonce: Date.now(), fit });
      }}
      onPrinted={() => answer({ designId: opened.designId, params })}
      onClose={() => setNext(null)}
    />
  );

  return (
    <section className="page page-configurator">
      <header className="page-head configurator-head">
        <div>
          <h1>{title}</h1>
          {description?.meta.description ? <p className="page-sub">{String(description.meta.description)}</p> : null}
        </div>
        <div className="configurator-meta">
          <span className="version-pill" title={copy("configurator.versionHint")}>
            v{opened.version}
          </span>
          {opened.deprecated && <span className="version-pill is-warn">{copy("configurator.deprecated")}</span>}
          {!!downloadCount && <span className="version-pill download-pill">↓ {formatDownloads(copy, downloadCount, locale)}</span>}
          {newer && brand.config.features.saveConfigs && (
            <button type="button" className="cfg-btn" onClick={upgrade}>
              {copy("configurator.update", { version: newer })}
            </button>
          )}
        </div>
      </header>

      <Frame
        aside={
          printNotes ? (
            <div className="print-notes">
              <strong>{copy("configurator.printNotes")}</strong> {printNotes}
            </div>
          ) : undefined
        }
      >
        <div className="configurator-layout">
          <div className="configurator-stage">
            {wide && entryBar}
            <Viewport
              preset={scene}
              modelColor={brand.config.theme.colors.model}
              // Feature edges always on, as the studio's "edges" view: they read the shape better.
              display={{ edges: true, edgeColor: brand.config.theme.colors.text }}
              showLabels={brand.config.features.floatingLabels}
              label={t("configurator.viewport", { title })}
              busyLabel={t("configurator.busy")}
              orbitHint
            />
            <div className="stage-stats">
              {stats ? (
                <>
                  <StatChip kind="size" value={sizeText} />
                  <StatChip kind="volume" value={`${(stats.volume / 1000).toFixed(1)} cm³`} />
                  <span className="stage-stat stage-stat-telemetry">{copy("configurator.telemetry", { tris: stats.numTri.toLocaleString(locale), ms: stats.ms.toFixed(0) })}</span>
                </>
              ) : (
                <span>{status === "error" ? error : copy("configurator.building")}</span>
              )}
            </div>
            {wide && actions}
            {wide && nextCard}
          </div>

          <aside className="configurator-panel">
            {reminder && <DownloadReminder record={reminder} onYes={() => void reminderYes(reminder)} onNo={() => void reminderNo(reminder)} onDismiss={() => (answer(reminder), setReminder(null))} />}
            {gallery ? (
              <div className="panel-title-row panel-tabs" role="tablist" aria-label={title}>
                <button type="button" role="tab" id="cfg-tab-params" aria-controls="cfg-tabpanel-params" aria-selected={tab === "params"} tabIndex={tab === "params" ? 0 : -1} onKeyDown={onTabKey} className={`panel-tab${tab === "params" ? " is-active" : ""}`} onClick={() => setTab("params")}>
                  {copy("configurator.params")}
                </button>
                <button type="button" role="tab" id="cfg-tab-community" aria-controls="cfg-tabpanel-community" aria-selected={tab === "community"} tabIndex={tab === "community" ? 0 : -1} onKeyDown={onTabKey} className={`panel-tab${tab === "community" ? " is-active" : ""}`} onClick={() => setTab("community")}>
                  {copy("community.tab")}
                  {!!community.data?.items.length && <span className="panel-tab-count">{community.data.items.length}</span>}
                </button>
                {tab === "params" && !onModel && (
                  <button type="button" className="cfg-chip panel-tab-reset" onClick={reset} disabled={!dirty && !opened.saved}>
                    {copy("configurator.reset")}
                  </button>
                )}
              </div>
            ) : (
              <div className="panel-title-row">
                <h2>{copy("configurator.params")}</h2>
                <button type="button" className="cfg-chip" onClick={reset} disabled={!dirty && !opened.saved}>
                  {copy("configurator.reset")}
                </button>
              </div>
            )}

            <div className="panel-tabpanel" id="cfg-tabpanel-params" role={gallery ? "tabpanel" : undefined} aria-labelledby={gallery ? "cfg-tab-params" : undefined} hidden={tab !== "params"}>
              {/* A community bracket on the model locks the dimensions until the user goes back to theirs. */}
              {onModel && (
                <div className="panel-banner" role="status">
                  <span>{copy("community.locked")}</span>
                  <span className="panel-banner-actions">
                    <button type="button" className="cfg-btn cfg-btn-primary panel-adopt" onClick={adopt}>
                      {copy("community.adopt")}
                    </button>
                    <button type="button" className="cfg-text-btn" onClick={restore}>
                      ↺ {copy("community.restore")}
                    </button>
                  </span>
                </div>
              )}
              <FamilySwitch designId={opened.designId} />
              {description ? (
                <>
                  {!onModel && gallery && features.fitTags && <StartFromSet designId={opened.designId} items={community.data?.items} onStart={startFromSet} />}
                  {!onModel && <PresetBar label={copy("configurator.presets")} />}
                  <ParamForm disabled={!!onModel} />
                </>
              ) : (
                <PanelSkeleton />
              )}
              {features.saveConfigs && !onModel && (
                <MyBracket
                  designId={opened.designId}
                  version={opened.version}
                  saved={opened.saved}
                  autoName={autoName}
                  autoNameFor={(p) => (description ? configName(title, spec, p, description.defaults) : autoName)}
                  defaultsRef={defaultsRef}
                  communityItems={community.data?.items}
                  onShowEntry={openEntry}
                  announce={setLive}
                  tagRequest={tagRequest}
                />
              )}
            </div>
            {gallery && (
              <div className="panel-tabpanel" id="cfg-tabpanel-community" role="tabpanel" aria-labelledby="cfg-tab-community" hidden={tab !== "community"}>
                <CommunityPanel designId={opened.designId} titleOf={titleOf} variantOf={variantOf} onModelId={onModel?.id ?? null} onApply={pickEntry} initialId={opened.communityId} initialFit={opened.fit} header={!wide && entryBar} />
              </div>
            )}

            {showSaveJump && saveHidden && (
              <button
                type="button"
                className="cfg-chip panel-save-jump"
                onClick={() => document.querySelector(".panel-save")?.scrollIntoView({ block: "start", behavior: scrollBehavior() })}
              >
                {copy("configurator.saveHeading")} ↓ <span className="muted">{copy("configurator.saveJumpHint")}</span>
              </button>
            )}
            {!wide && nextCard}
            {!wide && actions}
          </aside>
        </div>
      </Frame>
    </section>
  );
}
